import './index.css';
import { useState, useEffect } from "react";
import { SessionAuth } from "supertokens-auth-react/recipe/session";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { ThirdPartyPreBuiltUI } from "supertokens-auth-react/recipe/thirdparty/prebuiltui";
import { EmailPasswordPreBuiltUI } from "supertokens-auth-react/recipe/emailpassword/prebuiltui";
import { EmailVerificationPreBuiltUI } from "supertokens-auth-react/recipe/emailverification/prebuiltui";
import { SuperTokensWrapper } from "supertokens-auth-react";
import { getSuperTokensRoutesForReactRouterDom } from "supertokens-auth-react/ui";
import * as reactRouterDom from "react-router-dom";
import Joyride, { STATUS } from 'react-joyride';

// import MainPage from "./components/MainPage";
import TwoColumnLayout from "./components/TwoColumnLayout";
import NewLandingPage from "./components/NewLandingPage";
import LandingPage from "./components/LandingPage";
import Navbar from './components/NavBar';
import Gallery from "./components/Gallery";
import MockupsPage from "./components/MockupsPage";
import PrintOptions from "./components/PrintOptions";
import ProductCatalogPage from "./components/ProductCatalogPage";
import ConfigureMockupPage from "./components/ConfigureMockupPage";
import ConfirmOrderPage from "./components/ConfirmOrderPage";
import PaymentPage from "./components/PaymentPage";
import PaymentSuccessPage from "./components/PaymentSuccessPage";
import OrderHistoryPage from "./components/OrderHistoryPage";
import OrderDetailsPage from "./components/OrderDetailsPage";
import AdminPage from "./components/AdminPage";
import RoleManagement from "./components/RoleManagement";
import AdminUserCredits from "./components/AdminUserCredits";
import AdminOrderManagement from "./components/AdminOrderManagement";
import OrderManagementPage from "./components/OrderManagementPage";
import ReadyMadeGallery from "./components/ReadyMadeGallery";
import AdminDesignManager from "./components/AdminDesignManager";
import { api, uploadImage } from "./apiClient";
import ReviewMockupPage from "./components/ReviewMockupPage"; // NEW IMPORT
import SchedulerStatusPage from "./components/SchedulerStatusPage";
import ProfilePage from "./components/ProfilePage";
import { TermsPage, PrivacyPolicyPage, RefundPolicyPage, AiToolTermsPage } from "./components/LegalPages";

import { landingTour } from "./components/tours/landing";
import { designTour } from "./components/tours/design";
import { galleryTour } from "./components/tours/gallery";
import { productsTour } from "./components/tours/products";
import { mockupsTour } from "./components/tours/mockups";
import { configureMockupTour } from "./components/tours/configureMockup";
import CreditPurchaseModal from "./components/CreditPurchaseModal";
import CrispChat from "./components/CrispChat";
import GoogleAnalytics from "./components/GoogleAnalytics";
import HelpPage from "./components/HelpPage";
import HelpManualPage from "./components/HelpManualPage";
import { TourProvider, useTour } from "./context/TourContext";

const styles = ["Default", "Anime", "Cyberpunk", "Sketch"];

function AppContent() {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState(styles[0]);
  const [model, setModel] = useState("local");
  const [imageUrl, setImageUrl] = useState(null);
  const [imageId, setImageId] = useState(null);
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageStyle, setImageStyle] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { run, steps, stopTour } = useTour();
  const isLandingPage = location.pathname === '/';

  const getObjectKeyFromUrl = (url) => {
    if (!url) return "";
    try {
      const path = new URL(url).pathname;
      return decodeURIComponent(path.split('/').pop() || "");
    } catch {
      return decodeURIComponent(String(url).split('/').pop()?.split('?')[0] || "");
    }
  };

  const resolveImageIdFromGallery = async (url) => {
    try {
      const response = await api.get('/gallery', { params: { limit: 200, offset: 0 } });
      const data = response.data;
      const images = Array.isArray(data) ? data : (data?.items ?? []);
      const targetKey = getObjectKeyFromUrl(url);
      const matched = images.find((img) => getObjectKeyFromUrl(img?.image_url) === targetKey);
      if (matched?.id != null) return matched.id;
      if (images[0]?.id != null) return images[0].id;
      return null;
    } catch (error) {
      console.error("Failed to resolve image id from gallery:", error);
      return null;
    }
  };

  const handleGenerate = async () => {
    if (!prompt) {
      alert("Please enter a prompt first!");
      return;
    }
    setLoading(true);
    try {
      // Trying likely endpoint structure. If 404, user may need to correct.
      const response = await api.post('/generate-image', {
        prompt,
        style,
        model
      });
      console.log("Generate response:", response.data);
      const data = response.data;
      const url = data?.image_url ?? data?.imageUrl ?? data?.url;
      if (url) setImageUrl(url);
      const returnedId = data?.id ?? data?.image_id ?? data?.imageId;
      if (returnedId != null) {
        setImageId(returnedId);
      } else if (url) {
        const resolvedId = await resolveImageIdFromGallery(url);
        if (resolvedId != null) setImageId(resolvedId);
      }
      if (data?.prompt != null) setImagePrompt(data.prompt);
      if (data?.style != null) setImageStyle(data.style);
    } catch (error) {
      console.error("Error generating image:", error);
      if (error.response && error.response.status === 403 && error.response.data.detail.includes("Insufficient credits")) {
        setShowCreditModal(true);
      } else {
        alert("Failed to generate image. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file, description) => {
    setLoading(true);
    try {
      const response = await uploadImage(file, { description: description || undefined });
      if (process.env.NODE_ENV === 'development') console.log("Upload response:", response);
      const url = response?.imageUrl ?? response?.image_url;
      if (url) setImageUrl(url);
      const returnedId = response?.id ?? response?.image_id ?? response?.imageId;
      if (returnedId != null) {
        setImageId(returnedId);
      } else if (url) {
        const resolvedId = await resolveImageIdFromGallery(url);
        if (resolvedId != null) setImageId(resolvedId);
      }
      if (response?.prompt != null) setImagePrompt(response.prompt);
      if (response?.style != null) setImageStyle(response.style);
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App font-sans min-h-screen overflow-x-hidden">
      <CrispChat />
      <GoogleAnalytics />
      <Joyride
        steps={steps}
        run={run}
        continuous
        showProgress
        showSkipButton
        callback={({ status }) => {
          if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            stopTour();
          }
        }}
        styles={{
          options: { primaryColor: '#2563eb', zIndex: 10000 },
        }}
      />
      <Navbar landingNav={isLandingPage} onOpenCreditModal={() => setShowCreditModal(true)} />
      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onSuccess={() => setShowCreditModal(false)}
      />
      <Routes>
        {getSuperTokensRoutesForReactRouterDom(reactRouterDom, [ThirdPartyPreBuiltUI, EmailPasswordPreBuiltUI, EmailVerificationPreBuiltUI])}
        <Route path="/" element={<NewLandingPage />} />
        <Route path="/old-landing-page" element={<LandingPage />} />
        <Route path="/design" element={
          <TwoColumnLayout
            prompt={prompt}
            setPrompt={setPrompt}
            style={style}
            setStyle={setStyle}
            model={model}
            setModel={setModel}
            loading={loading}
            handleGenerate={handleGenerate}
            handleUpload={handleUpload}
            imageUrl={imageUrl}
            setImageUrl={setImageUrl}
            imageId={imageId}
            imagePrompt={imagePrompt}
            imageStyle={imageStyle}
            styles={styles}
          />
        } />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/mockups" element={<MockupsPage />} />
        <Route path="/products" element={<ProductCatalogPage />} />
        <Route path="/configure-mockup" element={<ConfigureMockupPage />} />
        <Route path="/print-options" element={<PrintOptions />} />
        <Route path="/confirm-order/:mockupId" element={<ConfirmOrderPage />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/orders" element={<OrderHistoryPage />} />
        <Route path="/orders/:orderId" element={<OrderDetailsPage />} />
        <Route path="/review-mockup/:mockupId" element={<ReviewMockupPage />} />
        <Route path="/profile" element={<SessionAuth requireAuth={true}><ProfilePage /></SessionAuth>} />
        <Route path="/test-protected-fe" element={
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h1>Test Protected Route</h1>
            <button
              onClick={async () => {
                try {
                  const response = await api.get('/protected');
                  alert(JSON.stringify(response.data));
                } catch (error) {
                  alert("Error: " + error.message);
                  console.error("Error accessing protected route:", error);
                }
              }}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                marginTop: '20px'
              }}
            >
              Call Protected Backend
            </button>
          </div>
        } />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/roles" element={<RoleManagement />} />
        <Route path="/admin/user-credits" element={<AdminUserCredits />} />
        <Route path="/admin/orders" element={<AdminOrderManagement />} />
        <Route path="/admin/scheduler-status" element={<SessionAuth requireAuth={true}><SchedulerStatusPage /></SessionAuth>} />
        <Route path="/admin/designs" element={<SessionAuth requireAuth={true}><AdminDesignManager /></SessionAuth>} />
        <Route path="/ordermanagement" element={<SessionAuth><OrderManagementPage /></SessionAuth>} />
        <Route path="/designs" element={<ReadyMadeGallery />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/manual" element={<HelpManualPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/ai-tool-terms" element={<AiToolTermsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
      </Routes >
    </div >
  );
}

export default function App() {
  return (
    <SuperTokensWrapper>
      <BrowserRouter>
        <TourProvider>
          <AppContent />
        </TourProvider>
      </BrowserRouter>
    </SuperTokensWrapper>
  );
}
